import api from './api';

const fetchConfiguredIntegrationBySrmAccountAndIntegrationId = async (
  srmAccountId,
  integrationId,
) => {
  try {
    const { data } = await api.get(
      `/configured-integrations?filter[srm_account_id_eq]=${srmAccountId}&filter[integration_uuid_eq]=${integrationId}`,
    );

    return { data: data.data, totalRecords: data.meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchConfiguredIntegrationsChangeHistory = async (
  uuid,
  page = 1,
  pageSize = 20,
) => {
  try {
    const { data } = await api.get(
      `/configured-integrations/${uuid}/change-history`,
      { params: { 'page[number]': page, 'page[size]': pageSize } },
    );

    return { data: data.data, totalRecords: data.meta?.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchConfiguredIntegrations = async (
  uuid,
  page = 1,
  limit = 25,
  { search = null, oem = null, accountType = null } = {},
) => {
  try {
    const requestParams = {
      'page[number]': page,
      'page[size]': limit,
    };

    // Handle search filter
    if (search && search.trim() !== '') {
      requestParams['filter[by_srm_account_multi_search]'] = search;
    }

    // Handle oem filter - use bracket notation for arrays
    if (oem && Array.isArray(oem)) {
      const filteredOem = oem.filter(
        (value) => value !== 'all' && value !== '',
      );
      if (filteredOem.length > 0) {
        requestParams['filter[by_oem][]'] = filteredOem;
      }
    } else if (oem && oem !== 'all' && oem !== '') {
      requestParams['filter[by_oem]'] = oem;
    }

    // Handle accountType filter - use bracket notation for arrays
    if (accountType && Array.isArray(accountType)) {
      const filteredAccountType = accountType.filter(
        (value) => value !== 'all' && value !== '',
      );
      if (filteredAccountType.length > 0) {
        requestParams['filter[by_account_type][]'] = filteredAccountType;
      }
    } else if (accountType && accountType !== 'all' && accountType !== '') {
      requestParams['filter[by_account_type]'] = accountType;
    }

    const { data } = await api.get(
      `/configured-integrations?filter[integration_uuid_eq]=${uuid}`,
      {
        params: requestParams,
      },
    );

    return { data: data.data, totalRecords: data.meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchConfiguredIntegrationsByUuid = async (uuid) => {
  try {
    const { data } = await api.get(`/configured-integrations/${uuid}`);

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateConfiguredIntegration = async (uuid, params) => {
  try {
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations/${uuid}`,
      {
        data: {
          type: 'configured_integrations',
          attributes: params,
        },
      },
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const deleteConfiguredIntegrationByUuid = async (uuid) => {
  try {
    const response = await api.delete(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations/${uuid}`,
      { data: {} },
    );

    return response;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const invokeIntegration = async (uuid, payload, source) => {
  try {
    const response = await api.post(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations/${uuid}/invoke`,
      {
        data: {
          type: 'integration_invocation',
          attributes: {
            payload,
            source,
          },
        },
      },
    );

    return response;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const searchConfiguredIntegrationByLocationSearchTerm = async (
  integrationId,
  searchTerm,
) => {
  try {
    const { data } = await api.get('/configured-integrations', {
      params: {
        'filter[integration_uuid_eq]': integrationId,
        'filter[enabled_eq]': true,
        'filter[by_srm_account_multi_search]': searchTerm,
      },
    });

    return { data: data.data, totalRecords: data.meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const searchAllConfiguredIntegrationsByLocationSearchTerm = async (
  integrationId,
  searchTerm,
) => {
  try {
    const { data } = await api.get('/configured-integrations', {
      params: {
        'filter[integration_uuid_eq]': integrationId,
        'filter[by_srm_account_multi_search]': searchTerm,
        'filter[with_removed]': true,
      },
    });

    return { data: data.data, totalRecords: data.meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Unfiltered counterpart: a disabled location still gets removed by a Shared
// Settings cascade, so anything that has to warn about the blast radius cannot
// use the enabled-only lookup below.
const fetchAllConfiguredIntegrationsBySrmAccountId = async (srmAccountId) => {
  try {
    const { data } = await api.get(
      `/configured-integrations?filter[srm_account_id_eq]=${srmAccountId}`,
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchConfiguredIntegrationsBySrmAccountId = async (srmAccountId) => {
  try {
    const { data } = await api.get(
      `/configured-integrations?filter[srm_account_id_eq]=${srmAccountId}&filter[enabled_eq]=true`,
    );

    return { data: data.data, totalRecords: data.meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchConfiguredIntegrationsBySlug = async (slug) => {
  try {
    const { data } = await api.get(
      `/configured-integrations?filter[slug_eq]=${slug}`,
    );

    return { data: data.data || null };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

export default {
  fetchConfiguredIntegrationsChangeHistory,
  fetchConfiguredIntegrationBySrmAccountAndIntegrationId,
  fetchConfiguredIntegrations,
  fetchConfiguredIntegrationsByUuid,
  fetchConfiguredIntegrationsBySrmAccountId,
  fetchAllConfiguredIntegrationsBySrmAccountId,
  deleteConfiguredIntegrationByUuid,
  updateConfiguredIntegration,
  invokeIntegration,
  searchConfiguredIntegrationByLocationSearchTerm,
  searchAllConfiguredIntegrationsByLocationSearchTerm,
  fetchConfiguredIntegrationsBySlug,
};
